import { HttpProductImageRepository } from '@/infrastructure/persistence/HttpProductImageRepository';
import { HttpProductRepository } from '@/infrastructure/persistence/HttpProductRepository';
import { HttpTemplateRepository } from '@/infrastructure/persistence/HttpTemplateRepository';
import { PiniaCuttingShapeRepository } from '@/infrastructure/persistence/PiniaCuttingShapeRepository';
import { PiniaEntityRepository } from '@/infrastructure/persistence/PiniaEntityRepository';
import { PiniaProductStateRepository } from '@/infrastructure/persistence/PiniaProductStateRepository';
import { PiniaTemplateConfigurationRepository } from '@/infrastructure/persistence/PiniaTemplateConfigurationRepository';
import type { ICuttingShapeRepository } from '@/application/ports/ICuttingShapeRepository';
import type { IEntityRepository } from '@/application/ports/IEntityRepository';
import type { IProductImageRepository } from '@/application/ports/IProductImageRepository';
import type { IProductRepository } from '@/application/ports/IProductRepository';
import type { IProductStateRepository } from '@/application/ports/IProductStateRepository';
import type { ISelectionState } from '@/application/ports/ISelectionState';
import type { ITemplateConfigurationRepository } from '@/application/ports/ITemplateConfigurationRepository';
import type { ITemplateRepository } from '@/application/ports/ITemplateRepository';

// Single source of truth for infrastructure singletons. All repository
// adapters live behind lazy getters so PiniaEntityRepository can defer
// construction until Pinia is installed (its constructor calls
// useTemplateStore()), and HTTP adapters stay uniform with the same
// shape.
//
// Consumers import `container` and access repositories by name:
//     container.productRepository.get('cup_33')
//
// Tests use `vi.mock('@/infrastructure/container', ...)` to swap the
// whole object at import time — no runtime reset is needed. If an
// integration test ever needs one, add it here then.

let _cuttingShapeRepository: ICuttingShapeRepository | undefined;
let _entityRepository: (IEntityRepository & ISelectionState) | undefined;
let _productImageRepository: IProductImageRepository | undefined;
let _productRepository: IProductRepository | undefined;
let _productStateRepository: IProductStateRepository | undefined;
let _templateConfigurationRepository: ITemplateConfigurationRepository | undefined;
let _templateRepository: ITemplateRepository | undefined;

const container = {
    get cuttingShapeRepository(): ICuttingShapeRepository {
        _cuttingShapeRepository ??= new PiniaCuttingShapeRepository();
        return _cuttingShapeRepository;
    },
    get entityRepository(): IEntityRepository & ISelectionState {
        _entityRepository ??= new PiniaEntityRepository();
        return _entityRepository;
    },
    get productImageRepository(): IProductImageRepository {
        _productImageRepository ??= new HttpProductImageRepository();
        return _productImageRepository;
    },
    get productRepository(): IProductRepository {
        _productRepository ??= new HttpProductRepository();
        return _productRepository;
    },
    get productStateRepository(): IProductStateRepository {
        _productStateRepository ??= new PiniaProductStateRepository();
        return _productStateRepository;
    },
    get templateConfigurationRepository(): ITemplateConfigurationRepository {
        _templateConfigurationRepository ??= new PiniaTemplateConfigurationRepository();
        return _templateConfigurationRepository;
    },
    get templateRepository(): ITemplateRepository {
        _templateRepository ??= new HttpTemplateRepository();
        return _templateRepository;
    },
};

export { container };
